import React, { useEffect, useState, useCallback } from 'react'
import { useLocation, useNavigate } from 'react-router-dom'
import { useProjectContext } from '../../../context/ProjectContext'
import SimpleAccordion from '../../ui_reusable/SimpleAccordion'
import { HeaderContainer } from '../../ui_reusable/styles/PlacementMisc.styles'
import { SectionTitle } from '../../ui_reusable/styles/SectionTitle.styles'
import { useForm, FormProvider, useWatch } from 'react-hook-form'
import ClientProjectPicker from '../../comp_reusable/navigation/ClientProjectPicker'
import { Message } from 'rsuite'
import useStandardToaster from '../../ui_reusable/StandardToaster'
import DriversForm, { DRIVER_PAGE_SIZE } from './build_model/DriversForm'
import OptimizeParametersForm from './build_model/OptimizeParametersForm'
import RunModel from './build_model/RunModel'
import ImpactParametersForm from './build_model/ImpactParametersForm'
import useProjectAccess from './build_model/utils/logic/useProjectAccess'
import { useClientContext } from '../../../context/ClientContext'
import { ROUTED_REPORT_MODES } from '../../reports/types/ReportTypes'
import {
  ImpactArtifact,
  OptimizeArtifact,
  ImpactArtifactRequest,
  OptimizeArtifactRequest,
  Project,
  ProjectSolution,
  ProjectSolutionDriver,
} from '../../../api/generated/models'
import ChannelsForm from './build_model/ChannelsForm'
import { useV2ProjectsSolutionsChannelsRetrieve } from '../../../api/generated/endpoints/projects/projects'
import { useDatasetsRegionDatasetsList } from '../../../api/generated/endpoints/region-datasets/region-datasets'
import { StashedDriver } from './build_model/types/StashedDriver'

const BuildModelPage = () => {
  const { pushToast } = useStandardToaster()
  const navigate = useNavigate()
  const location = useLocation()
  const {
    solutionId,
    artifact,
    mode,
  }: {
    solutionId?: number
    artifact?: ImpactArtifact | OptimizeArtifact
    mode?: string
  } = location.state

  const [projectSolution, setProjectSolution] = useState<
    ProjectSolution | undefined
  >(undefined)
  const [pickerDrivers, setPickerDrivers] = useState<ProjectSolutionDriver[]>(
    [],
  )
  const [projectSolutionDrivers, setProjectSolutionDrivers] = useState<
    ProjectSolutionDriver[]
  >([])
  const [driversTotal, setDriversTotal] = useState(0)
  const [driversPage, setDriversPage] = useState(1)
  const [driversSearch, setDriversSearch] = useState('')
  const [driversSort, setDriversSort] = useState<{
    key: string
    direction: 'asc' | 'desc'
  } | null>(null)
  const [driversShowSelected, setDriversShowSelected] = useState(false)
  const [driversAvailableFirst, setDriversAvailableFirst] = useState(false)
  const [driversLoading, setDriversLoading] = useState(false)
  /** Selectable driver ids from the last select-all fetch (current filters). */
  const [driversSelectAllIds, setDriversSelectAllIds] = useState<number[]>([])
  const [stashedDrivers, setStashedDrivers] = useState<StashedDriver[]>([])
  const projectsWithoutAccess = useProjectAccess({ solutionId, mode, artifact })
  const { selectedProject, setSelectedProject } = useProjectContext()
  const { selectedClient, setSelectedClient } = useClientContext()

  const methods = useForm<OptimizeArtifactRequest | ImpactArtifactRequest>({
    defaultValues: {
      name: artifact?.name ?? '',
      timeframe: artifact?.timeframe,
      project_solution_id: artifact?.project_solution?.id,
      dependent_driver_id: artifact?.dependent_driver?.id,
      independent_driver_ids:
        // For OPTIMIZE: load from control_drivers (form uses independent_driver_ids internally)
        // For IMPACT: load from independent_drivers
        artifact?.project_solution?.solution?.name === 'OPTIMIZE'
          ? ((artifact as any)?.control_drivers?.map((d: any) => d.id) ?? [])
          : ((artifact as any)?.independent_drivers?.map((d: any) => d.id) ??
            []),
      control_driver_ids:
        // Only used for OPTIMIZE, but gets set from independent_driver_ids in RunModel
        [],
      channel_driver_inputs:
        (artifact as any)?.channel_drivers?.map((cd: any) => ({
          channel_id: cd.channel?.id,
          spend_dataset_id: cd.spend_dataset?.id ?? null,
          impression_dataset_id: cd.impression_dataset?.id ?? null,
          reach_dataset_id: cd.reach_dataset?.id ?? null,
          frequency_dataset_id: cd.frequency_dataset?.id ?? null,
          adstock_decay: cd.adstock_decay ?? 'auto',
        })) ?? [],
      client_id: artifact?.client?.id ?? selectedClient?.id,
      parameters: {
        model_data_frequency: (artifact as any)?.parameters
          ?.model_data_frequency,
        business_goal:
          (artifact as any)?.parameters?.business_goal ?? 'increase',
        aggregation_method: (artifact as any)?.parameters?.aggregation_method,
      },
      optimize_config: {
        timeframe: (artifact as any)?.optimize_config?.timeframe ?? {
          from: '',
          to: '',
        },
        dependent_driver_is_revenue:
          (artifact as any)?.optimize_config?.dependent_driver_is_revenue ??
          true,
        geo_granularity:
          (artifact as any)?.optimize_config?.geo_granularity ?? 'national',
        budget_type: (artifact as any)?.optimize_config?.budget_type ?? 'auto',
        budget_amount:
          (artifact as any)?.optimize_config?.budget_amount ?? null,
        max_lag: (artifact as any)?.optimize_config?.max_lag ?? 8,
        spend_constraint:
          (artifact as any)?.optimize_config?.spend_constraint ?? 0.2,
        hill_before_adstock:
          (artifact as any)?.optimize_config?.hill_before_adstock ?? false,
        roi_impression: (() => {
          const isRevenue =
            (artifact as any)?.optimize_config?.dependent_driver_is_revenue ??
            true
          const revenuePerKpiId =
            (artifact as any)?.optimize_config?.revenue_per_kpi_id ?? null
          const showRoi = isRevenue !== false || revenuePerKpiId != null
          if (!showRoi) return null
          return (
            (artifact as any)?.optimize_config?.roi_impression || 'standard'
          )
        })(),
        roi_reach: (() => {
          const isRevenue =
            (artifact as any)?.optimize_config?.dependent_driver_is_revenue ??
            true
          const revenuePerKpiId =
            (artifact as any)?.optimize_config?.revenue_per_kpi_id ?? null
          const showRoi = isRevenue !== false || revenuePerKpiId != null
          if (!showRoi) return null
          return (artifact as any)?.optimize_config?.roi_reach || 'standard'
        })(),
        roi_calculation_period:
          (artifact as any)?.optimize_config?.roi_calculation_period ??
          'optimize_period',
        model_data_frequency: (artifact as any)?.optimize_config
          ?.model_data_frequency,
        revenue_per_kpi_id:
          (artifact as any)?.optimize_config?.revenue_per_kpi_id ?? null,
        target_roi: (artifact as any)?.optimize_config?.target_roi ?? null,
        target_mroi: (artifact as any)?.optimize_config?.target_mroi ?? null,
        expected_media_contribution:
          (artifact as any)?.optimize_config?.expected_media_contribution ??
          0.4,
      },
      // Set regional selection fields synchronously to avoid race conditions with driver fetch
      selected_region_ids:
        (artifact as any)?.optimize_config?.geo_granularity === 'regional' &&
        Array.isArray((artifact as any)?.optimize_config?.regions) &&
        (artifact as any)?.optimize_config?.regions.length > 0
          ? (artifact as any)?.optimize_config?.regions
          : ['national'],
      is_national_selected:
        (artifact as any)?.optimize_config?.geo_granularity !== 'regional',
    } as any,
  })

  useEffect(() => {
    // This effect runs specifically to set the project when entering edit mode directly.
    if (
      mode === ROUTED_REPORT_MODES.EDIT_MODEL &&
      artifact &&
      !selectedProject
    ) {
      if (artifact.client && artifact.project_solution?.project) {
        setSelectedClient({
          ...artifact.client,
          id: artifact.client.id,
          client_id: artifact.client?.id?.toString() ?? '',
          kam_id: 0,
        })
        setSelectedProject({
          id: artifact.project_solution.project.id,
          name: artifact.project_solution.project.name,
          logo: artifact.project_solution.project.logo,
          kam: artifact.project_solution.project.kam,
          kam_id: artifact.project_solution.project.kam?.id ?? 0,
          client: artifact.client,
          client_id: artifact.client.id!,
          project_id:
            artifact.project_solution.project.id?.toString() ?? '',
        } as Project)
      } else {
        console.error(
          'Artifact is missing client or project data in edit mode.',
        )
        pushToast(
          <Message type="error">
            Could not load model data: missing client or project.
          </Message>,
          {
            placement: 'topCenter',
            duration: 5000,
          },
        )
        navigate('/models')
      }
    }
  }, [
    artifact,
    mode,
    selectedProject,
    setSelectedClient,
    setSelectedProject,
    navigate,
    pushToast,
  ])

  // Keep client_id in sync with selectedClient
  // This handles the case where selectedClient is undefined when the form initializes
  // but becomes available later (e.g., after user picks a client)
  useEffect(() => {
    if (selectedClient?.id && !methods.getValues('client_id')) {
      methods.setValue('client_id', selectedClient.id)
    }
  }, [selectedClient, methods])

  const dependentDriverId = useWatch({
    control: methods.control,
    name: 'dependent_driver_id',
  })

  // Clear stashed drivers when dependent driver changes
  // This ensures stash indicators don't persist incorrectly when user selects a new outcome variable
  useEffect(() => {
    if (dependentDriverId) {
      setStashedDrivers([])
    }
  }, [dependentDriverId])

  const projectSolutionId = useWatch({
    control: methods.control,
    name: 'project_solution_id',
  })

  const timeframe = useWatch({ control: methods.control, name: 'timeframe' })

  // Watch the user-selected frequency so we can re-fetch drivers when the user changes it
  const frequency = useWatch({
    control: methods.control,
    name: 'parameters.model_data_frequency',
  })

  const geoGranularity = useWatch({
    control: methods.control,
    name: 'optimize_config.geo_granularity',
  })

  const selectedRegionIds = useWatch({
    control: methods.control,
    name: 'selected_region_ids' as any,
  })

  const regionsParam =
    selectedRegionIds &&
    Array.isArray(selectedRegionIds) &&
    selectedRegionIds.length > 0
      ? selectedRegionIds.filter((id) => id !== 'national').join(',')
      : ''

  const isOptimize = projectSolution?.solution?.name === 'OPTIMIZE'
  const projectId = selectedProject?.id

  // OPTIMIZE only: regional datasets for the dependent driver (geo granularity).
  const { data: dependentDriverRegionalDatasets } =
    useDatasetsRegionDatasetsList(
      { parent_id: dependentDriverId },
      {
        query: {
          enabled: !!dependentDriverId && isOptimize,
        },
      },
    )

  // TODO:
  // If Opinion will start using aggregation to create national dataset then uncomment
  //
  // Extract region IDs from dependent driver's regional datasets
  // const dependentDriverRegionIds =
  //   dependentDriverRegionalDatasets?.data
  //     ?.map((rd) => rd.region?.id)
  //     .filter((id): id is number => id !== undefined)
  //     .join(',') ?? ''
  // When is_national is true and dependent driver has regions, pass those regions
  // to enable backend aggregation of regional data into national dataset
  // const channelsRegionsParam =
  //   geoGranularity === 'national' && dependentDriverRegionIds
  //     ? dependentDriverRegionIds
  //     : regionsParam

  // OPTIMIZE only: validate selected regions when outcome variable changes.
  // If the new outcome variable doesn't have all the currently selected regions,
  // clear regions and switch to national mode.
  useEffect(() => {
    if (!isOptimize) return

    // Skip if national is selected - no need to validate
    const currentIsNational = methods.getValues('is_national_selected' as any)
    if (currentIsNational) return

    // Skip if no regional datasets loaded yet
    if (!dependentDriverRegionalDatasets?.data) return

    const currentSelectedRegions =
      methods.getValues('selected_region_ids' as any) || []
    const regionOnlyIds = currentSelectedRegions.filter(
      (id: string | number) => id !== 'national',
    )

    // Skip if no regions selected (national mode)
    if (regionOnlyIds.length === 0) return

    // Get available region IDs from the new outcome variable
    const availableRegionIds = new Set(
      dependentDriverRegionalDatasets.data
        .map((rd) => rd.region?.id)
        .filter((id): id is number => id !== undefined),
    )

    // Find regions that are no longer available
    const missingRegions = regionOnlyIds.filter(
      (regionId: number) => !availableRegionIds.has(regionId),
    )

    if (missingRegions.length > 0) {
      // Some regions are not available - switch to national mode
      methods.setValue('selected_region_ids' as any, ['national'], {
        shouldValidate: true,
      })
      methods.setValue('is_national_selected' as any, true, {
        shouldValidate: true,
      })
      methods.setValue('optimize_config.geo_granularity', 'national', {
        shouldValidate: true,
      })

      pushToast(
        <Message type="warning" style={{ maxWidth: '25rem' }}>
          The selected regions are not available for the new outcome variable.
          Geographical granularity has been reset to National.
        </Message>,
        { placement: 'topCenter', duration: 10000 },
      )
    }
    // methods / pushToast are unstable identities; omit from deps.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOptimize, dependentDriverRegionalDatasets, dependentDriverId])

  const { data: channelsData } = useV2ProjectsSolutionsChannelsRetrieve(
    projectId?.toString() ?? '',
    solutionId?.toString() ?? '',
    {
      frequency: frequency,
      from: timeframe?.from,
      to: timeframe?.to,
      regions: regionsParam,
      // TODO: If Opinion will start using aggregation to create national dataset then uncomment
      // regions: channelsRegionsParam,
      is_national: geoGranularity === 'national',
    },
    {
      query: {
        // Wait for outcome — channels validation depends on geo/outcome context.
        enabled:
          !!projectId && !!solutionId && isOptimize && !!dependentDriverId,
      },
    },
  )

  // Solution detail — only when project / solution identity changes (not on
  // frequency / timeframe / dependent-driver updates).
  useEffect(() => {
    if (!projectId || !solutionId) return

    const abortController = new AbortController()

    const fetchSolution = async () => {
      try {
        const authHeaders = {
          Authorization: `Bearer ${localStorage.getItem('token')}`,
        }
        const solutionUrl = `${process.env.REACT_APP_API_URL}/api/projects/${projectId}/solutions/${solutionId}?include_drivers=false`
        const pickerUrl = `${process.env.REACT_APP_API_URL}/api/projects/${projectId}/solutions/${solutionId}/all-drivers?ordering=-id`

        const [solutionResponse, pickerResponse] = await Promise.all([
          fetch(solutionUrl, {
            headers: authHeaders,
            signal: abortController.signal,
          }),
          fetch(pickerUrl, {
            headers: authHeaders,
            signal: abortController.signal,
          }),
        ])

        if (!solutionResponse.ok) {
          throw new Error('Failed to fetch project solution')
        }

        const solutionJson = await solutionResponse.json()
        setProjectSolution(solutionJson.data)
        methods.setValue('project_solution_id', solutionJson.data.id)

        if (pickerResponse.ok) {
          const pickerJson = await pickerResponse.json()
          setPickerDrivers(pickerJson.data || [])
        } else {
          setPickerDrivers([])
        }

        setDriversPage(1)
        setDriversSearch('')
        setDriversShowSelected(false)
      } catch (error: any) {
        if (error?.name === 'AbortError') return
        console.error('Error fetching project solution:', error)
        pushToast(
          <Message type="error">
            Error loading data: {error.message}
          </Message>,
          {
            placement: 'topCenter',
            duration: 5000,
          },
        )
        navigate('/models')
      }
    }

    fetchSolution()
    return () => abortController.abort()
    // methods / navigate / pushToast are unstable identities; omit from deps.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [projectId, solutionId])

  // Validated drivers — server-paginated Select drivers grid.
  const independentDriverIds = useWatch({
    control: methods.control,
    name: 'independent_driver_ids',
  })
  const selectedDriverFilterKey = driversShowSelected
    ? [...(independentDriverIds || []), dependentDriverId]
        .filter(Boolean)
        .map(Number)
        .sort((a, b) => a - b)
        .join(',')
    : ''

  const buildValidatedDriversParams = useCallback(
    (overrides?: { page?: number; limit?: number }) => {
      const params = new URLSearchParams()
      params.set('page', String(overrides?.page ?? driversPage))
      params.set('limit', String(overrides?.limit ?? DRIVER_PAGE_SIZE))
      if (frequency) {
        params.set('frequency', String(frequency))
      }
      if (timeframe?.from && timeframe?.to) {
        params.set('from', timeframe.from)
        params.set('to', timeframe.to)
      }
      if (geoGranularity === 'national') {
        params.set('is_national', 'true')
      } else if (regionsParam) {
        params.set('regions', regionsParam)
        params.set('is_national', 'false')
      }
      if (dependentDriverId) {
        params.set('dependent_driver', String(dependentDriverId))
      }
      if (projectSolution?.solution?.name === 'OPTIMIZE') {
        params.set('no_channels', 'true')
      }
      if (driversSearch.trim()) {
        params.set('search', driversSearch.trim())
      }
      if (driversShowSelected) {
        params.set('dataset_ids', selectedDriverFilterKey)
      }
      if (driversSort?.key) {
        params.set(
          'ordering',
          `${driversSort.direction === 'desc' ? '-' : ''}${driversSort.key}`,
        )
      }
      if (driversAvailableFirst) {
        params.set('available_first', 'true')
      }
      return params
    },
    [
      driversPage,
      frequency,
      timeframe?.from,
      timeframe?.to,
      geoGranularity,
      regionsParam,
      dependentDriverId,
      projectSolution?.solution?.name,
      driversSearch,
      driversSort,
      driversShowSelected,
      selectedDriverFilterKey,
      driversAvailableFirst,
    ],
  )

  useEffect(() => {
    if (!projectId || !solutionId || !projectSolution) return

    const abortController = new AbortController()

    const fetchDrivers = async () => {
      setDriversLoading(true)
      try {
        const params = buildValidatedDriversParams()
        const driversUrl = `${process.env.REACT_APP_API_URL}/api/v2/projects/${projectId}/solutions/${solutionId}/drivers?${params.toString()}`

        const driversResponse = await fetch(driversUrl, {
          headers: {
            Authorization: `Bearer ${localStorage.getItem('token')}`,
          },
          signal: abortController.signal,
        })

        if (!driversResponse.ok) throw new Error('Failed to fetch drivers')
        const driversJson = await driversResponse.json()
        const freshDrivers = driversJson.data || []
        setProjectSolutionDrivers(freshDrivers)
        setDriversTotal(driversJson.total ?? freshDrivers.length)

        // Un-stash only when the driver is present on this page and active again.
        setStashedDrivers((prev) => {
          if (prev.length === 0) return prev

          const stillIncompatible = prev.filter((stashed) => {
            const freshDriver = freshDrivers.find(
              (psd: any) => psd.driver?.id === stashed.id,
            )
            if (!freshDriver) return true
            return freshDriver.driver?.is_active === false
          })

          return stillIncompatible.length === prev.length
            ? prev
            : stillIncompatible
        })

        // Only deactivate selected drivers that appear on this page as inactive.
        // Missing from the page ≠ missing from the catalog (pagination).
        try {
          const currentIndependent =
            methods.getValues('independent_driver_ids') || []

          if (currentIndependent && currentIndependent.length > 0) {
            const incompatibleOnPage = currentIndependent.filter(
              (driverId: number) => {
                const found = freshDrivers.find(
                  (psd: any) => psd.driver?.id === driverId,
                )
                return found && found.driver?.is_active === false
              },
            )

            if (incompatibleOnPage.length > 0) {
              const incompatibleIds = incompatibleOnPage.filter(
                (id: any) => id !== undefined,
              )

              if (incompatibleIds.length > 0) {
                const newStashed: StashedDriver[] = incompatibleIds.map(
                  (driverId: number) => {
                    const found = freshDrivers.find(
                      (psd: any) => psd.driver?.id === driverId,
                    )
                    return {
                      id: driverId,
                      reason: 'inactive_from_api' as const,
                      driverName: found?.driver?.name,
                    }
                  },
                )

                setStashedDrivers((prev) => {
                  const existingIds = new Set(prev.map((s) => s.id))
                  const toAdd = newStashed.filter((s) => !existingIds.has(s.id))
                  return [...prev, ...toAdd]
                })

                const newIndependent = currentIndependent.filter(
                  (driverId: number) => !incompatibleIds.includes(driverId),
                )
                methods.setValue('independent_driver_ids', newIndependent, {
                  shouldValidate: true,
                })

                const incompatibleNames = incompatibleOnPage
                  .map((driverId: number) => {
                    const driver = freshDrivers.find(
                      (psd: any) => psd.driver?.id === driverId,
                    )?.driver
                    return driver?.name || driverId
                  })
                  .join(', ')

                pushToast(
                  <Message type="warning" style={{ maxWidth: '25rem' }}>
                    The following explanatory variables are not supported for
                    the selected outcome variable / frequency and have been
                    removed: {incompatibleNames}
                  </Message>,
                  { placement: 'topCenter', duration: 10000 },
                )
              }
            }
          }
        } catch (e) {
          console.error('Error reconciling independent drivers:', e)
        }

      } catch (error: any) {
        if (error?.name === 'AbortError') return
        console.error('Error fetching drivers:', error)
        pushToast(
          <Message type="error">Error loading data: {error.message}</Message>,
          {
            placement: 'topCenter',
            duration: 5000,
          },
        )
        navigate('/models')
      } finally {
        if (!abortController.signal.aborted) {
          setDriversLoading(false)
        }
      }
    }

    fetchDrivers()
    return () => abortController.abort()
    // methods / navigate / pushToast are unstable identities; omit from deps.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    projectId,
    solutionId,
    projectSolution?.id,
    projectSolution?.solution?.name,
    timeframe?.from,
    timeframe?.to,
    frequency,
    dependentDriverId,
    regionsParam,
    geoGranularity,
    driversPage,
    driversSearch,
    driversSort,
    driversShowSelected,
    driversAvailableFirst,
  ])

  // Reset to page 1 when validation filters change (not when only page changes).
  useEffect(() => {
    setDriversPage(1)
    setDriversSelectAllIds([])
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    timeframe?.from,
    timeframe?.to,
    frequency,
    dependentDriverId,
    regionsParam,
    geoGranularity,
    projectSolution?.id,
  ])

  useEffect(() => {
    setDriversSelectAllIds([])
  }, [driversSearch, driversShowSelected])

  const driversSelectAllActive =
    driversSelectAllIds.length > 0 &&
    driversSelectAllIds.every((id) =>
      (independentDriverIds || []).includes(id),
    )

  const handleDriversPageChange = useCallback((page: number) => {
    setDriversPage(page)
  }, [])

  const handleDriversSearchChange = useCallback((term: string) => {
    setDriversSearch(term)
    setDriversPage(1)
  }, [])

  const handleDriversSortChange = useCallback(
    (nextSort: { key: string; direction: 'asc' | 'desc' } | null) => {
      setDriversSort(nextSort)
      setDriversPage(1)
    },
    [],
  )

  const handleDriversShowSelectedChange = useCallback((next: boolean) => {
    setDriversShowSelected(next)
    setDriversPage(1)
  }, [])

  const handleDriversAvailableFirstChange = useCallback((next: boolean) => {
    setDriversAvailableFirst(next)
    setDriversPage(1)
  }, [])

  const handleDriversSelectAllToggle = useCallback(async () => {
    if (!projectId || !solutionId || !projectSolution || !dependentDriverId) {
      return
    }

    const current = methods.getValues('independent_driver_ids') || []
    const allCurrentlySelected =
      driversSelectAllIds.length > 0 &&
      driversSelectAllIds.every((id) => current.includes(id))

    if (allCurrentlySelected) {
      const toRemove = new Set(driversSelectAllIds)
      methods.setValue(
        'independent_driver_ids',
        current.filter((id: number) => !toRemove.has(id)),
        { shouldValidate: true },
      )
      setStashedDrivers([])
      setDriversSelectAllIds([])
      return
    }

    setDriversLoading(true)
    try {
      const limit = Math.min(
        Math.max(driversTotal || 9999, DRIVER_PAGE_SIZE),
        9999,
      )
      const params = buildValidatedDriversParams({ page: 1, limit })
      const driversUrl = `${process.env.REACT_APP_API_URL}/api/v2/projects/${projectId}/solutions/${solutionId}/drivers?${params.toString()}`

      const driversResponse = await fetch(driversUrl, {
        headers: {
          Authorization: `Bearer ${localStorage.getItem('token')}`,
        },
      })
      if (!driversResponse.ok) throw new Error('Failed to fetch drivers')
      const driversJson = await driversResponse.json()
      const allDrivers: ProjectSolutionDriver[] = driversJson.data || []

      const selectableIds = allDrivers
        .map((psd) => psd.driver)
        .filter(
          (driver): driver is NonNullable<typeof driver> =>
            !!driver?.id &&
            driver.id !== dependentDriverId &&
            (driver as { is_active?: boolean }).is_active !== false,
        )
        .map((driver) => driver.id as number)

      setDriversSelectAllIds(selectableIds)
      methods.setValue(
        'independent_driver_ids',
        Array.from(new Set([...current, ...selectableIds])),
        { shouldValidate: true },
      )
      setStashedDrivers([])
    } catch (error: any) {
      console.error('Error selecting all drivers:', error)
      pushToast(
        <Message type="error">
          Error selecting all drivers: {error.message}
        </Message>,
        { placement: 'topCenter', duration: 5000 },
      )
    } finally {
      setDriversLoading(false)
    }
    // methods / pushToast unstable; omit from deps.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    projectId,
    solutionId,
    projectSolution,
    driversSelectAllIds,
    driversTotal,
    buildValidatedDriversParams,
    dependentDriverId,
  ])

  // Default model name from outcome picker list (full set, not current page).
  useEffect(() => {
    if (
      mode === ROUTED_REPORT_MODES.EDIT_MODEL ||
      !dependentDriverId ||
      pickerDrivers.length === 0
    ) {
      return
    }
    const currentName = methods.getValues('name')
    if (currentName) return
    const match = pickerDrivers.find(
      (psd) => psd.driver?.id === dependentDriverId,
    )
    if (match?.driver?.name) {
      methods.setValue(
        'name',
        `${match.driver.name}_${new Date().getTime()}`,
      )
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dependentDriverId, pickerDrivers, mode])

  return (
    <FormProvider {...methods}>
      {selectedProject && projectSolutionId ? (
        <SimpleAccordion
          id={'buildPageSimpleAccordion'}
          defaultActiveKey={['1', '2', '3', '4']}
        >
          {projectSolution && (
            <>
              <HeaderContainer>
                <SectionTitle>
                  {projectSolution.solution?.name} model build
                </SectionTitle>
                <ClientProjectPicker
                  disabledItemValues={projectsWithoutAccess}
                />
              </HeaderContainer>
              {projectSolution.solution?.name === 'IMPACT' && (
                <ImpactParametersForm
                  projectSolutionDrivers={pickerDrivers}
                  projectSolution={projectSolution}
                />
              )}
              {isOptimize && (
                <OptimizeParametersForm
                  projectSolutionDrivers={pickerDrivers}
                  regionalDatasets={
                    dependentDriverRegionalDatasets?.data ?? []
                  }
                />
              )}
              {isOptimize && (
                <ChannelsForm channels={channelsData?.data ?? []} />
              )}
              <DriversForm
                projectSolutionDrivers={projectSolutionDrivers}
                solutionName={projectSolution.solution?.name}
                stashedDrivers={stashedDrivers}
                setStashedDrivers={setStashedDrivers}
                totalAvailable={driversTotal}
                serverPage={driversPage}
                loading={driversLoading}
                onPageChange={handleDriversPageChange}
                onSearchChange={handleDriversSearchChange}
                onSortChange={handleDriversSortChange}
                onSelectAllToggle={handleDriversSelectAllToggle}
                allSelected={driversSelectAllActive}
                showSelectedOnly={driversShowSelected}
                onShowSelectedChange={handleDriversShowSelectedChange}
                availableFirst={driversAvailableFirst}
                onAvailableFirstChange={handleDriversAvailableFirstChange}
              />
              <RunModel projectSolution={projectSolution} />
            </>
          )}
        </SimpleAccordion>
      ) : (
        <div>Loading...</div>
      )}
    </FormProvider>
  )
}
export default BuildModelPage
